import React from "react";
import styled from "styled-components";
import { FONTS, COLORS } from "../utils/styleConstants";
import { formatDateToCustomFormat } from '../utils/dateUtils';

const HistoryContainer = styled.div`
  background: white;
  border: none;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const HistoryTitle = styled.h3`
  display: none;
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  width: fit-content;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TimelineItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: fit-content;
  position: relative;
`;

const StatesContainer = styled.div`
  display: inline-block;
  width: auto;
`;

const StatesRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2rem;
  flex-wrap: wrap;
  width: auto;
`;

const LineSegmentsContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
  width: auto;
`;

const LineSegment = styled.div`
  height: ${(props) => (props.isLast ? "2px" : "1px")};
  background-color: ${(props) =>
    props.isLast ? COLORS.ACCENT : COLORS.PRIMARY};
  width: 100%;
  position: relative;
  margin: 0;
  align-self: flex-start;
`;

const DatesRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2rem;
  flex-wrap: wrap;
  width: auto;
`;

const StateItem = styled.div`
  position: relative;
  width: fit-content;
  flex: 0 0 auto;
  padding-right: ${(props) => (props.isLast ? "0" : "1.5rem")};

  &::after {
    content: "▶";
    color: ${(props) => (props.isLast ? COLORS.ACCENT : COLORS.PRIMARY)};
    font-size: 0.8rem;
    position: absolute;
    right: 8px;
    top: 0;
    display: ${(props) => (props.isLast ? "none" : "block")};

    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const DateItem = styled.div`
  width: fit-content;
  flex: 0 0 auto;
  text-align: left;
`;

const StatusText = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(props) => (props.isLast ? COLORS.ACCENT : COLORS.PRIMARY)};
  font-family: ${FONTS.SECONDARY};
  text-transform: capitalize;
`;

const StatusDate = styled.div`
  font-size: 0.8rem;
  color: ${(props) => (props.isLast ? COLORS.ACCENT : COLORS.PRIMARY)};
  font-family: ${FONTS.SECONDARY};
  line-height: 1.2;
`;



const formatDate = (dateString) => {
  if (!dateString) return { date: "", time: "" };
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
  const timeStr = date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return { date: dateStr, time: timeStr };
};

const getStatusText = (status) => {
  const statusMap = {
    draft: "Borrador",
    sent: "Enviado",
    accepted: "Aceptado",
    rejected: "Rechazado",
    expired: "Vencido",
    counteroffered: "Contraofertado",
    expiring_soon: "Por vencer",
    reformulating: "Reformulando",
    reformulated: "Reformulado",
    finalized: "Firmado",
    reformulation_accepted: "Aceptado",
    reformulation_rejected: "Rechazado",
    signing: "Firmando",
    partial_signed: "Firmado Parcialmente",
    signed: "Firmado",
  };
  return statusMap[status] || status;
};



const buildTimeline = (offer) => {
  const timeline = [];

  // 1. BORRADOR - Siempre es el primer estado (fecha de creación)
  if (offer.creationDate) {
    timeline.push({
      status: "draft",
      date: offer.creationDate,
      text: getStatusText("draft"),
    });
  }

  // 2. ENVIADO - Si la oferta fue enviada
  if (
    offer.sentDate ||
    [
      "sent",
      "accepted",
      "rejected",
      "counteroffered",
      "reformulated",
      "reformulation_accepted",
      "reformulation_rejected",
      "finalized",
      "expired",
    ].includes(offer.status)
  ) {
    timeline.push({
      status: "sent",
      date: offer.sentDate || offer.lastStatusUpdateDate || offer.creationDate,
      text: getStatusText("sent"),
    });
  }

  // 3. CONTRAOFERTADO - Si hay contraoferta
  if (
    offer.counteroffer_date ||
    offer.counteroffer_user_id ||
    offer.counteroffer_text ||
    [
      "counteroffered",
      "reformulated",
      "reformulation_accepted", 
      "reformulation_rejected"
    ].includes(offer.status)
  ) {
    timeline.push({
      status: "counteroffered",
      date: offer.counteroffer_date || offer.lastStatusUpdateDate,
      text: getStatusText("counteroffered"),
    });
  }

  // 4. REFORMULADO - Si hay reformulación
  if (
    offer.reformulation_date ||
    offer.reformulation_content ||
    offer.reformulationText ||
    [
      "reformulated",
      "reformulation_accepted",
      "reformulation_rejected"
    ].includes(offer.status)
  ) {
    timeline.push({
      status: "reformulated",
      date: offer.reformulation_date || offer.lastStatusUpdateDate,
      text: getStatusText("reformulated"),
    });
  }

  // 5. ESTADOS FINALES - Solo uno de estos puede existir por oferta
  
  // 5.1 Aceptado (oferta original)
  if (offer.status === "accepted") {
    timeline.push({
      status: "accepted",
      date: offer.acceptance_date || offer.acceptanceDate || offer.offer_response_date || offer.lastStatusUpdateDate,
      text: getStatusText("accepted"),
    });
  }
  
  // 5.2 Rechazado (oferta original)
  else if (offer.status === "rejected") {
    timeline.push({
      status: "rejected",
      date: offer.rejection_date || offer.rejectionDate || offer.offer_response_date || offer.lastStatusUpdateDate,
      text: getStatusText("rejected"),
    });
  }
  
  // 5.3 Reformulación aceptada
  else if (offer.status === "reformulation_accepted") {
    timeline.push({
      status: "reformulation_accepted",
      date: offer.reformulation_acceptance_date || offer.reformulation_response_date || offer.lastStatusUpdateDate,
      text: getStatusText("reformulation_accepted"),
    });
  }
  
  // 5.4 Estados de firma electrónica - Pueden seguir a accepted o reformulation_accepted
  else if (offer.status === "signing") {
    // Primero agregar el estado de aceptación previo
    if (offer.reformulation_acceptance_date || offer.reformulation_response_date) {
      // Vino de reformulación
      timeline.push({
        status: "reformulation_accepted",
        date: offer.reformulation_acceptance_date || offer.reformulation_response_date || offer.lastStatusUpdateDate,
        text: getStatusText("reformulation_accepted"),
      });
    } else {
      // Vino de aceptación directa
      timeline.push({
        status: "accepted",
        date: offer.acceptance_date || offer.acceptanceDate || offer.offer_response_date || offer.lastStatusUpdateDate,
        text: getStatusText("accepted"),
      });
    }
    
    // Agregar estado de firmando
    timeline.push({
      status: "signing",
      date: offer.signing_initiated_date || offer.lastStatusUpdateDate,
      text: getStatusText("signing"),
    });
  }
  
  // 5.5 Firmado parcialmente
  else if (offer.status === "partial_signed") {
    // Primero agregar el estado de aceptación previo
    if (offer.reformulation_acceptance_date || offer.reformulation_response_date) {
      // Vino de reformulación
      timeline.push({
        status: "reformulation_accepted",
        date: offer.reformulation_acceptance_date || offer.reformulation_response_date || offer.lastStatusUpdateDate,
        text: getStatusText("reformulation_accepted"),
      });
    } else {
      // Vino de aceptación directa
      timeline.push({
        status: "accepted",
        date: offer.acceptance_date || offer.acceptanceDate || offer.offer_response_date || offer.lastStatusUpdateDate,
        text: getStatusText("accepted"),
      });
    }
    
    // Agregar estado de firmando si existe fecha
    if (offer.signing_initiated_date) {
      timeline.push({
        status: "signing",
        date: offer.signing_initiated_date,
        text: getStatusText("signing"),
      });
    }
    
    // Agregar estado de firmado parcialmente
    timeline.push({
      status: "partial_signed",
      date: offer.lastStatusUpdateDate,
      text: getStatusText("partial_signed"),
    });
  }
  
  // 5.6 Firmado completamente
  else if (offer.status === "signed") {
    // Primero agregar el estado de aceptación previo
    if (offer.reformulation_acceptance_date || offer.reformulation_response_date) {
      // Vino de reformulación
      timeline.push({
        status: "reformulation_accepted",
        date: offer.reformulation_acceptance_date || offer.reformulation_response_date || offer.lastStatusUpdateDate,
        text: getStatusText("reformulation_accepted"),
      });
    } else {
      // Vino de aceptación directa
      timeline.push({
        status: "accepted",
        date: offer.acceptance_date || offer.acceptanceDate || offer.offer_response_date || offer.lastStatusUpdateDate,
        text: getStatusText("accepted"),
      });
    }
    
    // Agregar estado de firmando si existe fecha
    if (offer.signing_initiated_date) {
      timeline.push({
        status: "signing",
        date: offer.signing_initiated_date,
        text: getStatusText("signing"),
      });
    }
    
    // Agregar estado de firmado completamente
    timeline.push({
      status: "signed",
      date: offer.signing_completed_date || offer.lastStatusUpdateDate,
      text: getStatusText("signed"),
    });
  }
  
  // 5.7 Reformulación rechazada
  else if (offer.status === "reformulation_rejected") {
    timeline.push({
      status: "reformulation_rejected",
      date: offer.reformulation_rejection_date || offer.reformulation_response_date || offer.lastStatusUpdateDate,
      text: getStatusText("reformulation_rejected"),
    });
  }
  
  // 5.8 Finalizado/Firmado (legacy)
  else if (offer.status === "finalized") {
    timeline.push({
      status: "finalized",
      date: offer.finalizationDate || offer.lastStatusUpdateDate,
      text: getStatusText("finalized"),
    });
  }
  
  // 5.9 Vencido - Este puede ocurrir en cualquier momento después de enviado
  else if (offer.status === "expired") {
    timeline.push({
      status: "expired",
      date: offer.expirationDate || offer.deadlineToRespond || offer.lastStatusUpdateDate,
      text: getStatusText("expired"),
    });
  }

  // Ordenar timeline por fecha cronológica
  return timeline.sort((a, b) => new Date(a.date) - new Date(b.date));
};

const OfferTimeline = ({ offer }) => {
  if (!offer) return null;

  const timeline = buildTimeline(offer);

  if (!timeline.length) {
    return null;
  }

  return (
    <HistoryContainer>
      <HistoryTitle>Historial de estados</HistoryTitle>
      <TimelineContainer>
        {timeline.map((item, index) => {
          const { date, time } = formatDate(item.date);
          const isLastItem = index === timeline.length - 1;
          
          return (
            <TimelineItem key={index}>
              <StateItem isLast={isLastItem}>
                <StatusText isLast={isLastItem}>
                  {item.text}
                </StatusText>
              </StateItem>
              <LineSegment isLast={isLastItem} />
              <DateItem>
                <StatusDate isLast={isLastItem}>
                  {date}
                  <br />
                  {time}
                </StatusDate>
              </DateItem>
            </TimelineItem>
          );
        })}
      </TimelineContainer>
    </HistoryContainer>
  );
};

export default OfferTimeline;
